import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, X, Lock, Eye, FileText, CheckCircle2 } from 'lucide-react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-900/60 backdrop-blur-xs">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-100 flex flex-col max-h-[85vh] overflow-hidden my-auto"
        >
          {/* Header */}
          <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/80 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-xs">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">Kebijakan Privasi & Perlindungan Data</h3>
                <p className="text-xs text-slate-500">Standar Pengelolaan Metadata UIBN Indonesia</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body Content */}
          <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-600 leading-relaxed">
            <div className="p-4 rounded-xl bg-indigo-50/70 border border-indigo-100 flex items-start gap-3">
              <Lock className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-indigo-900 text-xs mb-1">
                  Komitmen Kerahasiaan & Keamanan Metadata
                </p>
                <p className="text-indigo-800 text-[11px]">
                  UIBN Indonesia berkomitmen penuh untuk melindungi hak privasi penulis, penerbit, dan pengguna.
                  Informasi naskah dan metadata diterbitkan secara transparan sesuai standar identifikasi buku digital.
                </p>
              </div>
            </div>

            {/* Section 1 */}
            <div className="space-y-2">
              <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                1. Pengumpulan Data & Metadata
              </h4>
              <p>Kami mengumpulkan data berikut saat Anda mendaftar atau menguji registrasi buku:</p>
              <ul className="list-disc pl-5 space-y-1 text-slate-600">
                <li><strong className="text-slate-800">Informasi Akun:</strong> Nama lengkap, alamat email, peran pengguna (Penulis, Penerbit, Admin, atau Pembaca).</li>
                <li><strong className="text-slate-800">Metadata Buku:</strong> Judul, sub-judul, nama penulis, penerbit, tahun terbit, nomor edisi, kualifikasi bahasa, daftar isi, dan sinopsis.</li>
                <li><strong className="text-slate-800">Data Pemindaian:</strong> Catatan histori pemindaian barcode/QR code untuk tujuan verifikasi keaslian naskah.</li>
              </ul>
            </div>

            {/* Section 2 */}
            <div className="space-y-2">
              <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Eye className="w-4 h-4 text-indigo-600" />
                2. Penggunaan & Publikasi Data
              </h4>
              <p>Data yang dikumpulkan dipergunakan untuk:</p>
              <ul className="list-disc pl-5 space-y-1 text-slate-600">
                <li>Menerbitkan nomor identifikasi unik UIBN menggunakan algoritma checksum Modulo-10.</li>
                <li>Menyediakan layanan verifikasi publik melalui barcode 1D (Code 128) dan QR Code 2D.</li>
                <li>Menampilkan direktori katalog naskah bagi masyarakat dan penerbit terverifikasi.</li>
                <li>Mengirimkan notifikasi perubahan status pengajuan buku (Draft, Diajukan, Disetujui, atau Ditolak).</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="space-y-2">
              <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Lock className="w-4 h-4 text-indigo-600" />
                3. Keamanan Data & Proteksi Kata Sandi
              </h4>
              <p>
                Seluruh kata sandi disimpan dalam bentuk terenkripsi (*salted hash* `bcrypt`). Sistem kami dilengkapi dengan pembatas laju akses (*rate limiting*), perlindungan *Content Security Policy* (CSP), serta validasi hak akses bertingkat (*Role-Based Access Control*).
              </p>
            </div>

            {/* Section 4 */}
            <div className="space-y-2">
              <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                4. Hak Cipta & Kepemilikan Naskah
              </h4>
              <p>
                Pendaftaran UIBN tidak mengalihkan hak cipta karya dari penulis/penerbit kepada UIBN Indonesia. Seluruh hak cipta atas isi buku tetap sepenuhnya berada di tangan penulis dan penerbit yang bersangkutan.
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 text-[11px] text-slate-500">
              Terakhir diperbarui: <span className="font-medium text-slate-700">25 September 2026</span>. Jika Anda memiliki pertanyaan seputar Kebijakan Privasi, silakan hubungi tim UIBN Indonesia di <span className="text-indigo-600 font-medium">privasi@uibn.or.id</span>.
            </div>
          </div>

          {/* Footer button */}
          <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end shrink-0">
            <button
              onClick={onClose}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white transition cursor-pointer shadow-xs"
            >
              Saya Mengerti & Setuju
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
